import { useMemo, useRef, useState } from "react";
import { useTranslations } from "@zapier/i18n";

import {
  Button,
  ModalDialogBody,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogRoot,
  Spacer,
  Text,
} from "@zapier/design-system";

import {
  getMessageFromNetworkError,
  useDeleteZaps,
  useDisableZap,
  useMoveZapsToFolder,
  useUserAccountRole,
  useZapApprovals,
  useIsApprovalRequiredForZap,
} from "@zapier/client";
import {
  useNotification,
  ApprovalType,
  ApprovalRequiredOperation,
} from "@zapier/utils";
import { Approval } from "@zapier/bff";
import styles from "./ZapDeleteModal.module.css";
import { ZapModal } from "../types";
import { FlexSpacer } from "../../common/FlexSpacer/FlexSpacer";
import { useBulkActionLimitGuard } from "../../../hooks/useBulkActionLimitGuard";
import { TrashNotification } from "../../common/TrashNotification/TrashNotification";

interface Props {
  currentItemFolderId?: string;
  currentItemIds: string[];
  enabledZapIds?: string[] | undefined;
  /** The single Zap's title. Only rendered when `currentItemIds.length === 1` — plural copy is driven by `count` instead. */
  currentItemTitle: string;
  isOpen: boolean;
  isZapEnabled: boolean;
  isZapPublished: boolean;
  onSettled?: () => void;
  setApprovalType?: (approvalType: ApprovalType) => void;
  setCurrentModal: (modal?: ZapModal | undefined) => void;
  setIsOpen: (isOpen: boolean) => void;
  projectId?: string;
  folderId?: string; // used for cache invalidation
}

interface ContentProps extends Props {
  onDelete: () => void;
  isApprovalRequired?: boolean;
  isDeleting: boolean;
  pendingDeleteApproval?: Approval;
  userPendingApproval?: boolean;
  isBulk: boolean;
}

interface onDeleteOptions {
  currentItemFolderId: string;
  /** May differ from the modal's current currentItemIds if reopened for another Zap. */
  itemIds: string[];
}

/** Order-independent id comparison. */
function hasSameIds(a: string[], b: string[]): boolean {
  return a.length === b.length && [...a].sort().join() === [...b].sort().join();
}

// An empty shown list doesn't count as "different": a bulk delete prunes its
// own rows from the selection before this runs.
function isShowingDifferentZaps(
  shownItemIds: string[],
  trackedItemIds: string[],
): boolean {
  return shownItemIds.length > 0 && !hasSameIds(shownItemIds, trackedItemIds);
}

function ZapDeleteModalContent({
  currentItemIds,
  currentItemTitle,
  isApprovalRequired,
  isBulk,
  isDeleting,
  isOpen,
  onDelete,
  pendingDeleteApproval,
  setIsOpen,
  userPendingApproval,
}: ContentProps) {
  const t = useTranslations("zap-delete-modal");
  const { customuserId: userId } = useUserAccountRole();

  const deleteCopy = useMemo(() => {
    // If delete requires an approval, show request to delete copy
    // Only applies for singular deletes, user cannot request to delete multiple zaps
    if (isApprovalRequired && !isBulk) {
      const requestToDeleteCopy =
        pendingDeleteApproval &&
        pendingDeleteApproval.requestedByUserId === userId
          ? t("buttons.cancel-request-to-delete")
          : t("buttons.request-to-delete");

      return requestToDeleteCopy;
    }

    return t("buttons.move-to-trash", { count: currentItemIds?.length || 0 });
  }, [isApprovalRequired, pendingDeleteApproval, userId, t, currentItemIds]);

  // Blocked while an approval the current user can't act on is pending.
  const isSubmitBlockedByApproval = Boolean(
    (isApprovalRequired &&
      pendingDeleteApproval &&
      pendingDeleteApproval.requestedByUserId !== userId &&
      !isBulk) ||
    (isApprovalRequired &&
      userPendingApproval &&
      !pendingDeleteApproval &&
      !isBulk),
  );
  const isSubmitDisabled = isSubmitBlockedByApproval || isDeleting;

  return (
    <ModalDialogRoot isOpen={isOpen} onOpenChange={setIsOpen}>
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- the form deliberately stops click/keydown propagation to keep the modal open and submit on Enter */}
      <form
        onSubmit={(event) => {
          // Without this, the browser navigates before the delete resolves.
          event.preventDefault();
          onDelete();
        }}
        onClick={(event) => event.stopPropagation()} // prevent the modal from closing when clicking inside the form
        onKeyDown={
          // Enter already submits via onSubmit; this only blocks it when disabled.
          (event) => {
            if (event.key !== "Enter") {
              return;
            }

            if (isSubmitDisabled) {
              event.preventDefault();
            }
          }
        }
      >
        <ModalDialogHeader textAlign="start">
          {t("header.move-to-trash-title", {
            count: currentItemIds.length,
            title: currentItemTitle,
          })}
        </ModalDialogHeader>
        <ModalDialogBody textAlign="start">
          <div className={styles.body}>
            <Text>
              {t("body.deletion-explanation", {
                count: currentItemIds.length,
              })}
            </Text>
            <Spacer height={12} />
            <Text>{t("body.connected-assets")}</Text>
          </div>
        </ModalDialogBody>
        <ModalDialogFooter>
          {/* Explicit type="button": Cancel must never submit the form. */}
          <Button
            type="button"
            variant="tertiary"
            isDisabled={isDeleting}
            onPress={() => setIsOpen(false)}
          >
            {t("buttons.cancel")}
          </Button>

          <FlexSpacer />

          {/* No onPress: it submits the form, avoiding a double mutation. */}
          <Button
            variant="danger"
            type="submit"
            isDisabled={isSubmitDisabled}
            isPending={isDeleting}
          >
            {deleteCopy}
          </Button>
        </ModalDialogFooter>
      </form>
    </ModalDialogRoot>
  );
}
export const ZapDeleteModal = ({
  currentItemFolderId,
  currentItemIds,
  currentItemTitle,
  projectId,
  enabledZapIds,
  isOpen,
  isZapEnabled,
  isZapPublished,
  onSettled,
  setApprovalType,
  setCurrentModal,
  setIsOpen,
  folderId,
}: Props) => {
  const t = useTranslations("zap-delete-modal");
  const notifyIfOverBulkLimit = useBulkActionLimitGuard("delete");

  const { createNotification } = useNotification();
  const { deleteZapsAsync } = useDeleteZaps({ folderId });
  const { disableZap } = useDisableZap();
  const { moveZapsToFolder } = useMoveZapsToFolder();
  // Tracks ids, not a boolean, so a pending delete doesn't disable the modal
  // after it's reopened for a different Zap (this instance persists across opens).
  const [deletingItemIds, setDeletingItemIds] = useState<string[] | null>(null);
  const isDeleting =
    deletingItemIds !== null &&
    !isShowingDifferentZaps(currentItemIds, deletingItemIds);

  // Lets a delete settling after a reopen detect it's no longer being shown.
  const currentItemIdsRef = useRef(currentItemIds);
  currentItemIdsRef.current = currentItemIds;

  const isBulk = currentItemIds.length > 1;

  const zapId =
    currentItemIds.length === 1 ? String(currentItemIds[0]) : undefined;
  const { pendingDeleteApproval, userPendingApproval } = useZapApprovals({
    zapId,
  });
  const isApprovalRequired = useIsApprovalRequiredForZap({
    zapId,
    isZapPublished,
    operation: ApprovalRequiredOperation.Delete,
  });

  // This callback needs to happen outside of the modal so that it can
  // still display a notification when the request completes after the modal has closed.
  const handleDelete = async () => {
    // Guard before the disable loop, so no zaps are turned off for a delete that can't proceed.
    if (notifyIfOverBulkLimit(currentItemIds.length)) {
      setIsOpen(false);
      return;
    }

    if (isApprovalRequired && setApprovalType && !isBulk) {
      setApprovalType(ApprovalType.Delete);
      setCurrentModal(ZapModal.Approval);
      return;
    }

    // If Zap(s) are enabled, disable it before deleting
    if (isZapEnabled && enabledZapIds) {
      for (const zapId of enabledZapIds) {
        await disableZap({
          zapId,
        });
      }
    }

    // Captured once: currentItemIds could change if the modal reopens mid-delete.
    const itemIdsBeingDeleted = currentItemIds;

    // Awaited rather than fired with onSuccess/onError, so the modal closes
    // only once the request actually resolves.
    setDeletingItemIds(itemIdsBeingDeleted);
    try {
      await deleteZapsAsync({
        zapIds: itemIdsBeingDeleted,
        projectId,
      });

      // TODO MNG-4354 - Add undo functionality when deleting project Zaps
      createNotification({
        message: (
          <TrashNotification
            message={
              projectId
                ? t("notifications.success.moved-to-trash-no-undo", {
                    count: itemIdsBeingDeleted.length,
                    zapTitle: currentItemTitle,
                  })
                : t("notifications.success.moved-to-trash", {
                    count: itemIdsBeingDeleted.length,
                    zapTitle: currentItemTitle,
                  })
            }
            onUndo={
              projectId
                ? undefined
                : () =>
                    currentItemFolderId &&
                    handleUndoDelete({
                      currentItemFolderId,
                      itemIds: itemIdsBeingDeleted,
                    })
            }
          />
        ),
        type: "success",
      });
    } catch (error: any) {
      const message = getMessageFromNetworkError(error);
      const customMessage = message.includes("Access denied")
        ? t("errors.unable-to-delete-message")
        : message;

      createNotification({
        title: t("errors.unable-to-delete-title", {
          count: itemIdsBeingDeleted.length,
          zapTitle: currentItemTitle,
        }),
        message: customMessage,
        type: "error",
      });
    } finally {
      // Only clear this delete's own entry, not one started after a reopen.
      setDeletingItemIds((trackedItemIds) =>
        trackedItemIds && hasSameIds(trackedItemIds, itemIdsBeingDeleted)
          ? null
          : trackedItemIds,
      );

      // Skip only when the modal has moved on to a different Zap, which makes
      // this a stale completion for the old one.
      if (
        !isShowingDifferentZaps(currentItemIdsRef.current, itemIdsBeingDeleted)
      ) {
        onSettled?.();
        setIsOpen(false);
      }
    }
  };

  const handleUndoDelete = ({
    currentItemFolderId,
    itemIds,
  }: onDeleteOptions) => {
    moveZapsToFolder(
      {
        folderId: currentItemFolderId,
        zapIds: itemIds,
      },
      {
        onSuccess: () => {
          createNotification({
            message: (
              <Text>
                {t("notifications.success.restored", {
                  count: itemIds.length,
                  zapTitle: currentItemTitle,
                })}
              </Text>
            ),
            type: "success",
          });
        },
        onError: (error: any) => {
          createNotification({
            title: t("errors.unable-to-restore-title", {
              count: itemIds.length,
              zapTitle: currentItemTitle,
            }),
            message: getMessageFromNetworkError(error),
            type: "error",
          });
        },
        onSettled: () => {
          onSettled?.();
        },
      },
    );

    setIsOpen(false);
  };

  // Do not display the modal if isOpen==false
  if (!isOpen) {
    return;
  }

  return (
    <ZapDeleteModalContent
      currentItemIds={currentItemIds}
      currentItemTitle={currentItemTitle}
      isApprovalRequired={isApprovalRequired}
      isBulk={isBulk}
      isDeleting={isDeleting}
      isOpen={isOpen}
      isZapEnabled={isZapEnabled}
      isZapPublished={isZapPublished}
      onDelete={handleDelete}
      pendingDeleteApproval={pendingDeleteApproval}
      setCurrentModal={setCurrentModal}
      setIsOpen={setIsOpen}
      userPendingApproval={userPendingApproval}
    />
  );
};
